import React, { useState } from "react";
import { BellRing, X, Mail } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { CATEGORY_NAMES, SUBCATEGORY_NAMES } from "@/lib/categories";

/**
 * "Sledovat podobné inzeráty" – sledování kategorie (volitelně podkategorie / lokality).
 *
 * Props: category, subcategory (optional), location (optional), onClose
 */
export default function WatchCategoryModal({ category, subcategory, location, onClose }) {
  const [email, setEmail] = useState(localStorage.getItem("stb_buyer_email") || "");
  const [includeSub, setIncludeSub] = useState(!!subcategory);
  const [includeLoc, setIncludeLoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/watchers/category", {
        category,
        subcategory: includeSub && subcategory ? subcategory : null,
        location: includeLoc && location ? location : null,
        email: email.trim(),
      });
      localStorage.setItem("stb_buyer_email", email.trim());
      setDone(true);
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="watch-category-modal"
    >
      <div className="stb-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="stb-h text-xl text-white inline-flex items-center gap-2">
            <BellRing size={18} className="text-[#FF6B00]" /> Sledovat podobné inzeráty
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white">
            <X size={18} />
          </button>
        </div>
        {done ? (
          <div className="mt-4 text-center" data-testid="watch-cat-done">
            <p className="stb-h text-[#22C55E] text-lg">Hotovo ✓</p>
            <p className="text-[#A3A3A3] text-sm mt-1" data-testid="watch-done">
              Vždy, když přibyde nový inzerát v této kategorii, dostaneš e-mail.
            </p>
            <button onClick={onClose} className="stb-btn stb-btn-primary mt-4">Zavřít</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-3 space-y-2">
            <p className="text-xs text-[#A3A3A3]">
              Kategorie: <strong className="text-white">{CATEGORY_NAMES[category] || category}</strong>
            </p>
            {subcategory && (
              <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSub}
                  onChange={(e) => setIncludeSub(e.target.checked)}
                  data-testid="watch-cat-include-sub"
                />
                Jen podkategorie:{" "}
                <strong className="text-white">{SUBCATEGORY_NAMES[subcategory] || subcategory}</strong>
              </label>
            )}
            {location && (
              <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeLoc}
                  onChange={(e) => setIncludeLoc(e.target.checked)}
                  data-testid="watch-cat-include-loc"
                />
                Jen v lokalitě: <strong className="text-white">{location}</strong>
              </label>
            )}
            <div className="relative pt-1">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="email"
                className="stb-input pl-9"
                placeholder="tvuj@email.cz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="watch-cat-email"
              />
            </div>
            {err && <p className="text-[#FF6B00] text-xs" data-testid="watch-cat-error">{err}</p>}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={busy}
                className="stb-btn stb-btn-primary flex-1"
                data-testid="watch-cat-submit"
              >
                {busy ? "Posílám…" : "Sledovat kategorii"}
              </button>
              <button type="button" onClick={onClose} className="stb-btn stb-btn-ghost">Zrušit</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
